import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Entry, EntryKind } from '@/types/api';
import { statedKind } from '@/utils/entryKind';
import {
  type Command,
  type DictationSession,
  type Draft,
  useDictation,
  type VisitRecordPart,
} from '../../dictation';
import type { useChartEntries } from './useChartEntries';

type Data = ReturnType<typeof useChartEntries>;

/**
 * Dictation wired to this patient's entries and transcripts. A procedure is
 * recorded as `mode` (existing state or performed work) unless the clause says
 * otherwise ("stara plomba"). "Undo" takes back the last batch. A spoken price
 * goes to `onPrice` (the visit's price field; the doctor confirms it), the
 * anamnesis and diagnosis codes to `onRecord` (the visit's protocol record).
 */
export function useChartDictation(
  data: Data,
  onSession: (session: DictationSession) => void,
  mode: EntryKind,
  onPrice: (price: number) => void,
  onRecord: (part: VisitRecordPart) => void
) {
  const { t } = useTranslation();
  const lastBatch = useRef<Entry[]>([]);
  const { save, reject } = data;

  const onDrafts = useCallback(
    async (drafts: Draft[], sessionId: string) => {
      const kinded = drafts.map((d) => ({ ...d, kind: statedKind(d.transcript, d.tooth) ?? mode }));
      lastBatch.current = await save(kinded, 'DICTATION', sessionId);
    },
    [save, mode]
  );

  const undo = useCallback(async () => {
    for (const e of lastBatch.current) if (!e.local) await reject(e, t('chart.undoReason'));
    lastBatch.current = [];
  }, [reject, t]);

  const onCommand = useCallback(
    async (command: Command) => {
      if (command === 'undo') await undo();
    },
    [undo]
  );

  return useDictation({ onDrafts, onCommand, onPrice, onSession, onRecord });
}
